/**
 * Revision session configurator for the "By topic" tab. Mirrors the shape of
 * the lesson-based `SessionCreator` (picker → question-type filter → max-
 * questions slider → start button) but sources its data from the curriculum
 * tree rather than the learner's enrolled content.
 *
 * Flow:
 *   1. `TopicPicker` — learner selects one or more curriculum nodes
 *      (multi-select, subtree-scoped; the backend expands via ltree).
 *   2. `useReviseScopePreview(nodeIds)` — round-trip to the server to get the
 *      per-type revisable step breakdown for the selection. Drives the filter
 *      UI with real counts (matching the lesson-based picker's UX).
 *   3. `QuestionTypeFilter` + slider — same components as the "By content"
 *      flow; UI is visually identical.
 *   4. Start — submit `{ nodeIds, questionTypes, maxQuestions }` to the
 *      revise-session endpoint. Server combines and caps per filters.
 *
 * Both the filter and slider hide until the learner has at least one topic
 * selected; collapsing smoothly via the same motion pattern SessionCreator
 * uses keeps the two tabs feeling like siblings.
 */
import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { Button, Slider } from '@/interfaces/learner/ui';
import { useReviseScopePreview } from '@/shared/api/hooks';
import QuestionTypeFilter from './QuestionTypeFilter';
import TopicPicker from './TopicPicker';
import { ALL_QUESTION_TYPES } from './questionTypes';

const EASE = [0.16, 1, 0.3, 1] as const;
const DEFAULT_MAX_QUESTIONS = 20;

export interface TopicSessionCreatorProps {
  onStart: (config: { nodeIds: string[]; questionTypes: string[]; maxQuestions: number }) => void;
  onCancel: () => void;
  isCreating: boolean;
  error?: string | null;
}

export default function TopicSessionCreator({
  onStart,
  onCancel,
  isCreating,
  error,
}: TopicSessionCreatorProps) {
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set());
  const [selectedQuestionTypes, setSelectedQuestionTypes] = useState<Set<string>>(
    new Set(ALL_QUESTION_TYPES)
  );
  const [maxQuestions, setMaxQuestions] = useState(DEFAULT_MAX_QUESTIONS);

  // Stable sorted array for the preview hook's query key.
  const nodeIdList = useMemo(() => [...selectedNodeIds].sort(), [selectedNodeIds]);
  const { data: preview } = useReviseScopePreview(nodeIdList);

  // Pull type + count info out of the preview into the shapes QuestionTypeFilter
  // expects. Counts are post-subtree-expansion so they reflect what the
  // session will actually include.
  const { availableTypes, typeCounts, totalAvailable } = useMemo(() => {
    const types = new Set<string>();
    const counts = new Map<string, number>();
    let total = 0;
    if (preview) {
      for (const { type, count } of preview.types) {
        if (count > 0) {
          types.add(type);
          if (selectedQuestionTypes.has(type)) {
            counts.set(type, count);
            total += count;
          }
        }
      }
    }
    return { availableTypes: types, typeCounts: counts, totalAvailable: total };
  }, [preview, selectedQuestionTypes]);

  // Clamp `maxQuestions` at render time rather than syncing state via an
  // effect. When the learner unchecks types and `totalAvailable` shrinks
  // below the slider value, we just display (and submit) the clamped value.
  // No state-sync → no cascading render → no lint violation.
  const effectiveMax = Math.min(maxQuestions, Math.max(1, totalAvailable));
  const hasSelection = selectedNodeIds.size > 0;
  const showTypes = hasSelection && availableTypes.size > 0;
  const showSlider = hasSelection && totalAvailable > 0;

  const handleToggleType = (type: string) => {
    setSelectedQuestionTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        // Keep at least one type selected — the lesson-based picker enforces
        // the same invariant, the filter UI disables the last-selected pill.
        if (next.size > 1) next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const handleStart = () => {
    if (selectedNodeIds.size === 0) {
      toast.info('Pick at least one subject');
      return;
    }
    onStart({
      nodeIds: [...selectedNodeIds],
      questionTypes: [...selectedQuestionTypes],
      maxQuestions: effectiveMax,
    });
  };

  const cancelLabel = 'Cancel';
  const startLabel = isCreating
    ? 'Starting…'
    : !hasSelection
      ? 'Pick subjects to start'
      : totalAvailable === 0
        ? 'No matching questions'
        : selectedNodeIds.size === 1
          ? `Revise ~${effectiveMax} questions`
          : `Revise ${selectedNodeIds.size} subjects`;

  return (
    <div>
      <p className="mb-4" style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
        Pick one or more subjects from your library — we&apos;ll pull every revisable question
        tagged to them (including sub-subjects) into one session.
      </p>

      <div className="flex flex-col gap-5">
        <TopicPicker selectedNodeIds={selectedNodeIds} onSelectionChange={setSelectedNodeIds} />

        <AnimatePresence initial={false}>
          {showTypes && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: EASE }}
              style={{ overflow: 'hidden' }}
            >
              <QuestionTypeFilter
                selectedTypes={selectedQuestionTypes}
                availableTypes={availableTypes}
                typeCounts={typeCounts}
                onToggle={handleToggleType}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence initial={false}>
          {showSlider && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: EASE }}
              style={{ overflow: 'hidden' }}
            >
              <div className="flex flex-col gap-4">
                <Slider
                  label="Number of Questions"
                  minValue={1}
                  maxValue={Math.max(1, totalAvailable)}
                  step={1}
                  value={effectiveMax}
                  output={effectiveMax}
                  onChange={setMaxQuestions}
                />

                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
                  ~{effectiveMax} questions from {selectedNodeIds.size}{' '}
                  {selectedNodeIds.size === 1 ? 'subject' : 'subjects'}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {error && (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-error)', margin: 0 }}>
            {error}
          </p>
        )}

        <div className="flex gap-3 justify-end">
          <Button variant="ghost" size="sm" onPress={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            variant="primary"
            size="sm"
            isDisabled={!hasSelection || totalAvailable === 0 || isCreating}
            onPress={handleStart}
          >
            {startLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
